import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ArticleCard from "@/components/feature/ArticleCard";
import Reveal from "@/components/base/Reveal";
import { articles, categories } from "@/mocks/news";

const PAGE_SIZE = 6;

export default function FeedSection() {
  const [activeSlug, setActiveSlug] = useState("all");
  const [keyword, setKeyword] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return articles.filter((item) => {
      const matchCategory = activeSlug === "all" || item.categorySlug === activeSlug;
      if (!matchCategory) return false;
      if (!kw) return true;
      return (
        item.title.toLowerCase().includes(kw) ||
        item.excerpt.toLowerCase().includes(kw) ||
        item.tags.join(" ").toLowerCase().includes(kw)
      );
    });
  }, [activeSlug, keyword]);

  const visible = filtered.slice(0, visibleCount);

  const handleTab = (slug: string) => {
    setActiveSlug(slug);
    setVisibleCount(PAGE_SIZE);
  };

  return (
    <section id="feed" className="border-b border-background-200 bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-[1400px] px-4 md:px-8">
        <Reveal>
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="w-full">
              <span className="label-font text-xs uppercase tracking-[0.24em] text-primary-600">
                Today Feed
              </span>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
                今日速递 · 新料实时更新
              </h2>
              <p className="mt-2.5 max-w-2xl text-sm leading-relaxed text-foreground-600">
                按栏目筛选当天的条目。凡是缺少第三方依据的说法，我们会在卡片与正文中标注为线索，而不是结论。
              </p>
            </div>
            <div className="flex w-full items-center rounded-md border border-background-300 bg-background-100 pl-3 lg:w-72">
              <span className="flex h-11 w-5 items-center justify-center">
                <i className="ri-search-2-line text-sm text-foreground-500" />
              </span>
              <input
                type="search"
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                  setVisibleCount(PAGE_SIZE);
                }}
                placeholder="站内搜索标题或标签"
                aria-label="站内搜索速递条目"
                className="h-11 flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
            </div>
          </div>
        </Reveal>

        <div className="no-scrollbar mt-7 flex gap-2 overflow-x-auto pb-1">
          <button
            type="button"
            onClick={() => handleTab("all")}
            className={`inline-flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              activeSlug === "all"
                ? "bg-foreground-950 text-background-50"
                : "border border-background-300 bg-background-100 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
            }`}
          >
            <i className="ri-apps-2-line" />
            全部条目
          </button>
          {categories.map((cat) => (
            <button
              key={cat.slug}
              type="button"
              onClick={() => handleTab(cat.slug)}
              className={`inline-flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                activeSlug === cat.slug
                  ? "bg-foreground-950 text-background-50"
                  : "border border-background-300 bg-background-100 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
              }`}
            >
              <i className={cat.icon} />
              {cat.name}
            </button>
          ))}
        </div>

        {visible.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((article, index) => (
              <Reveal key={article.id} delay={Math.min(index, 5) * 60}>
                <ArticleCard article={article} />
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-100/60 py-16">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200">
              <i className="ri-search-eye-line text-xl text-foreground-500" />
            </span>
            <p className="text-sm text-foreground-700">没有匹配的条目，换个关键词试试。</p>
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                handleTab("all");
              }}
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              重置筛选
            </button>
          </div>
        )}

        {visible.length > 0 && (
          <div className="mt-9 flex flex-col items-center gap-3">
            {visibleCount < filtered.length ? (
              <button
                type="button"
                onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                className="inline-flex h-11 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-foreground-950/15 bg-background-100 px-6 text-sm font-semibold text-foreground-900 transition-colors hover:border-primary-400 hover:text-primary-700"
              >
                <i className="ri-add-line" />
                加载更多条目（剩余 {filtered.length - visibleCount} 条）
              </button>
            ) : (
              <p className="text-xs text-foreground-500">
                已显示该筛选下的全部 {filtered.length} 条条目
              </p>
            )}
            <Link
              to="/category/all"
              className="text-xs text-primary-600 transition-colors hover:text-primary-700"
            >
              进入全部速递页，按时间查看 →
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}